import { camelCase } from "change-case";
import { camelize } from "fast-case";
import * as ValidatorJS from "validator";

import { sendRequest } from "../ApiUtil";

import { BridgeRequirements } from "@/screens/home/userpanel/UserPanel";
import { fieldsOnboarding, addressFields } from "@/utils/EnumsOnboarding";

interface requirementsError {
  description: string;
  field_name: string;
  status: string;
}

export const requiredCorporationDocs = [
  "INCORPORATION_DOCUMENTS",
  "ARTICLES_OF_INCORPORATION",
  "BENEFICIAL_OWNERSHIP_CERTIFICATE",
  "PASSPORT",
  "IDENTITY_CARD_FRONT",
  "IDENTITY_CARD_BACK",
  "DRIVERS_LICENCE_FRONT",
  "DRIVERS_LICENCE_BACK",
  "BANK_STATEMENT",
  "SELFIE",
];

export const requiredIndividualCorporateDocs = [
  "PASSPORT",
  "SELFIE",
  "IDENTITY_CARD_FRONT",
  "IDENTITY_CARD_BACK",
  "DRIVERS_LICENCE_FRONT",
  "DRIVERS_LICENCE_BACK",
  "BANK_STATEMENT",
];

export const requiredIndividualDocs = [
  "PASSPORT",
  "SELFIE",
  "IDENTITY_CARD_FRONT",
  "IDENTITY_CARD_BACK",
  "DRIVERS_LICENCE_FRONT",
  "DRIVERS_LICENCE_BACK",
  "BANK_STATEMENT",
];

export const personalDocuments = [
  "PASSPORT",
  "IDENTITY_CARD_FRONT",
  "IDENTITY_CARD_BACK",
  "DRIVERS_LICENCE_FRONT",
  "DRIVERS_LICENCE_BACK",
  "SELFIE",
];

export const allOnboardingDocuments = [
  "INCORPORATION_DOCUMENTS",
  "ARTICLES_OF_INCORPORATION",
  "BENEFICIAL_OWNERSHIP_CERTIFICATE",
  "ACCOUNT_AGREEMENT",
  "PASSPORT",
  "IDENTITY_CARD_FRONT",
  "IDENTITY_CARD_BACK",
  "DRIVERS_LICENCE_FRONT",
  "DRIVERS_LICENCE_BACK",
  "BANK_STATEMENT",
  "UTILITY_BILL",
  "LEASE_AGREEMENT",
  "PAYSLIP",
  "SELFIE",
  "STATEMENT_OF_FUNDS",
  "FORMATION_DOCUMENT",
  "OWNERSHIP_DOCUMENT",
  "PROOF_OF_ADDRESS_DOCUMENT",
];

export const corporatePdfDocuments = [
  "INCORPORATION_DOCUMENTS",
  "ARTICLES_OF_INCORPORATION",
  "BENEFICIAL_OWNERSHIP_CERTIFICATE",
  "LAYER2_ACCOUNT_AGREEMENT",
  "ACCOUNT_AGREEMENT",
  "LEASE_AGREEMENT",
];

export const imageFileTypes = [".JPEG", ".PNG"];
export const pdfFileTypes = [".PDF"];
export const defaultFileTypes = [...imageFileTypes, ".PDF"];

const defaultRoutes = ["/kyc", "/investmentprofile"];

export const individualRoutes = [
  ...defaultRoutes,
  "/individual/name",
  "/individual/employment",
  "/individual/information",
  "/individual/nationality",
  "/individual/termsandconditions",
];

export const corporationRoutes = [
  ...defaultRoutes,
  "/corporation/information",
  "/corporation/contact",
  "/corporation/description",
  "/corporation/localization",
  "/corporation/termsandconditions",
];

export const addressRoutes = [
  "/individual/address",
  "/corporation/registeredaddress",
  "/corporation/physicaladdress",
];

//Utils to Review.tsx

const camelizeWithDot = (fieldName: string) => {
  const parts = fieldName.split(".");
  const lastPart = parts[parts.length - 1];
  return camelize(lastPart);
};

export const extractErrors = (errors: any[]) => {
  const extractedErrors = errors
    .map((error: requirementsError) => {
      const fieldName = error.field_name.includes(".")
        ? camelizeWithDot(error.field_name)
        : camelize(error.field_name);
      const errorDescription = error.description ?? null;

      if (fieldsOnboarding.includes(fieldName)) {
        return { fieldName, errorDescription };
      }
      return null;
    })
    .filter(Boolean);

  return extractedErrors;
};

export async function submitUpdateIndividuals(
  individualsWithError: any[],
  individualsFieldsModified: any,
  requirementsId: string
) {
  individualsWithError.forEach(async (individual) => {
    const data: any = {};
    const individualId = individual.individual_id;
    const individualData = individualsFieldsModified[individualId];

    for (const [key, value] of Object.entries(individualData)) {
      if (addressFields.includes(key)) {
        data.mailingAddress = {
          ...data.mailingAddress,
          [key]: value,
        };
      } else {
        data[key] = value;
      }
    }

    try {
      await sendRequest(
        "patch",
        "/update-individual",
        {
          applicationId: requirementsId,
          individualId,
          update: {
            ...data,
          },
        },
        true
      );
    } catch (error: any) {
      console.log(error);
    }
  });
}

function processMissingFields(fields: string[]): string[] {
  return fields.map((field) => {
    const fieldSplit = field.split(".");
    return camelCase(fieldSplit[fieldSplit.length - 1]);
  });
}

export const validateAllFields = (
  requirements: BridgeRequirements,
  dataToRequest: Record<string, any>
): boolean => {
  let allFieldsValidated = true;
  let allDocumentsValidated = true;
  let allIndividualsValidated = true;

  if (requirements.missingFields && requirements.missingFields.length > 0) {
    const fields = processMissingFields(requirements.missingFields);
    const areFieldsValid = fields.every(
      (field) =>
        dataToRequest[field] !== undefined && dataToRequest[field] !== ""
    );
    if (!areFieldsValid) {
      allFieldsValidated = false;
    }
  }

  if (
    requirements.missingDocuments &&
    requirements.missingDocuments.length > 0
  ) {
    const documents = requirements.missingDocuments;
    const areDocumentsValid = documents.every(
      (document) =>
        dataToRequest[document.document] !== undefined &&
        dataToRequest[document.document] !== ""
    );

    if (!areDocumentsValid) {
      allDocumentsValidated = false;
    }
  }

  if (
    requirements.missingIndividualDetails &&
    requirements.missingIndividualDetails.length > 0
  ) {
    requirements.missingIndividualDetails.forEach((individual) => {
      if (individual.missingFields && individual.missingFields.length > 0) {
        const individualFields = individual.missingFields;
        const areIndividualFieldsValid = individualFields.every(
          (field) =>
            dataToRequest[`${individual.id}_${field}`] !== undefined &&
            dataToRequest[`${individual.id}_${field}`] !== ""
        );
        if (!areIndividualFieldsValid) {
          allIndividualsValidated = false;
        }
      }

      if (
        individual.missingDocuments &&
        individual.missingDocuments.length > 0
      ) {
        const individualDocuments = individual.missingDocuments;
        const areIndividualDocumentsValid = individualDocuments?.every(
          (document) =>
            dataToRequest[`${individual.id}_${document}`] !== undefined &&
            dataToRequest[`${individual.id}_${document}`] !== ""
        );
        if (!areIndividualDocumentsValid) {
          allIndividualsValidated = false;
        }
      }
    });
  }

  if (
    !allDocumentsValidated ||
    !allFieldsValidated ||
    !allIndividualsValidated
  ) {
    return false;
  }

  return true;
};

export function getCountryCode(
  requirements: BridgeRequirements
): ValidatorJS.PostalCodeLocale {
  if (requirements.missingFields) {
    const processedMissingFields = processMissingFields(
      requirements.missingFields
    );
    if (processedMissingFields.includes("postalCode")) {
      return requirements.countryCode as ValidatorJS.PostalCodeLocale;
    }
  }

  if (requirements.missingIndividualDetails) {
    for (const individual of requirements.missingIndividualDetails) {
      if (individual.missingFields) {
        const processedMissingFields = processMissingFields(
          individual.missingFields
        );
        if (processedMissingFields.includes("postalCode")) {
          return individual.countryCode as ValidatorJS.PostalCodeLocale;
        }
      }
    }
  }
}

export const fieldsToUseCodeInHookForm = [
  "countryCode",
  "country",
  "countryOfIncorporation",
  "nationality",
];
